import { computed, onMounted, reactive, ref, watch } from "vue";
import Swal from "sweetalert2";
import { useI18n } from "vue-i18n";

import customerService, { type Customer } from "@/services/customer";
import reportService, { type SalesReportItem } from "@/services/report";
import warehouseService, { type Warehouse } from "@/services/warehouse";

type SalesReportFilters = {
  warehouse_id: number | null;
  customer_id: number | null;
  status: string;
  from_date: string;
  to_date: string;
  per_page: number;
};

const createInitialFilters = (): SalesReportFilters => ({
  warehouse_id: null,
  customer_id: null,
  status: "",
  from_date: "",
  to_date: "",
  per_page: 10,
});

export function useSalesReport(reportType: "sales" | "pos") {
  const { t } = useI18n();
  const sales = ref<SalesReportItem[]>([]);
  const warehouses = ref<Warehouse[]>([]);
  const customers = ref<Customer[]>([]);
  const loading = ref(false);
  const exporting = ref(false);
  const searchQuery = ref("");
  const currentPage = ref(1);
  const total = ref(0);
  const filters = reactive<SalesReportFilters>(createInitialFilters());

  const isPosReport = reportType === "pos";
  const i18nScope = isPosReport ? "pos_sales_report_page" : "sales_report_page";
  const pageTitle = `${i18nScope}.title`;
  const exportFilename = isPosReport ? "pos_sales_report" : "sales_report";

  const pagination = computed(() => ({
    current: currentPage.value,
    pageSize: filters.per_page,
    total: total.value,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "30", "50", "100"],
    showTotal: (total: number, range: [number, number]) =>
      t(`${i18nScope}.pagination_total`, {
        start: range[0],
        end: range[1],
        total,
      }),
    locale: { items_per_page: t(`${i18nScope}.items_per_page`) },
  }));

  const queryParams = computed(() => ({
    page: currentPage.value,
    per_page: filters.per_page,
    search_query: searchQuery.value,
    warehouse_id: filters.warehouse_id || undefined,
    customer_id: filters.customer_id || undefined,
    status: filters.status || undefined,
    from_date: filters.from_date || undefined,
    to_date: filters.to_date || undefined,
    pos: isPosReport ? 1 : 0,
  }));

  const fetchFilterOptions = async () => {
    try {
      const [warehouseResults, customerResults] = await Promise.all([
        warehouseService.getWarehouses(),
        customerService.getCustomers(),
      ]);

      warehouses.value = warehouseResults;
      customers.value = customerResults;
    } catch (error) {
      console.error("Error loading sales report filters:", error);
    }
  };

  const fetchSalesReports = async () => {
    loading.value = true;
    try {
      const response = await reportService.getSalesReports(queryParams.value);
      sales.value = response.data;
      currentPage.value = response.current_page;
      total.value = response.total;
    } catch (error) {
      console.error("Error fetching sales reports:", error);
      Swal.fire({
        icon: "error",
        title: t(`${i18nScope}.load_failed`),
        text: t(`${i18nScope}.unable_to_load_report`),
      });
    } finally {
      loading.value = false;
    }
  };

  const handleTableChange = (page: number, pageSize: number) => {
    currentPage.value = page;

    if (pageSize !== filters.per_page) {
      filters.per_page = pageSize;
      currentPage.value = 1;
    }

    fetchSalesReports();
  };

  const applyFilters = () => {
    currentPage.value = 1;
    fetchSalesReports();
  };

  const resetFilters = () => {
    Object.assign(filters, createInitialFilters());
    currentPage.value = 1;
    fetchSalesReports();
  };

  const exportSalesReports = async () => {
    exporting.value = true;
    try {
      const response = await reportService.exportSalesReports({
        search_query: searchQuery.value,
        warehouse_id: filters.warehouse_id || undefined,
        customer_id: filters.customer_id || undefined,
        status: filters.status || undefined,
        from_date: filters.from_date || undefined,
        to_date: filters.to_date || undefined,
        pos: isPosReport ? 1 : 0,
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${exportFilename}_${new Date().getTime()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting sales reports:", error);
      Swal.fire({
        icon: "error",
        title: t(`${i18nScope}.export_failed`),
        text: t(`${i18nScope}.unable_to_export_report`),
      });
    } finally {
      exporting.value = false;
    }
  };

  watch(searchQuery, () => {
    currentPage.value = 1;
    fetchSalesReports();
  });

  onMounted(async () => {
    await fetchFilterOptions();
    await fetchSalesReports();
  });

  return {
    i18nScope,
    pageTitle,
    sales,
    warehouses,
    customers,
    loading,
    exporting,
    searchQuery,
    filters,
    pagination,
    handleTableChange,
    applyFilters,
    resetFilters,
    exportSalesReports,
  };
}
